/**
 * Append-only outreach touches on a per-event sponsor row.
 * sponsors/{sponsorRowId}/sponsorOutreachLog/{entryId}
 */

import {
  collection,
  addDoc,
  getDocs,
  query,
  orderBy,
  limit,
  updateDoc,
  doc,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { firestore } from '../firebase';
import { snapshotAuthor } from './sponsorNoteLog';

export const OUTREACH_SUBCOLLECTION = 'sponsorOutreachLog';

const ENTRY_LIMIT = 200;

export const OUTREACH_CHANNELS = [
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'in_person', label: 'In person' },
  { value: 'other', label: 'Other' },
];

export const OUTREACH_OUTCOMES = [
  { value: '', label: '(not set)' },
  { value: 'follow_up_later', label: 'Follow up later' },
  { value: 'maybe', label: 'Maybe' },
  { value: 'yes_attending_tbd', label: 'Yes (attendance TBD)' },
  { value: 'yes_committed', label: 'Yes (committed)' },
  { value: 'no', label: 'No / not this year' },
  { value: 'prizes_only', label: 'Prizes only' },
];

const ALLOWED_OUTCOMES = new Set(OUTREACH_OUTCOMES.map((o) => o.value).filter(Boolean));

const CHANNEL_VALUES = new Set(OUTREACH_CHANNELS.map((c) => c.value));

export function outreachOutcomeLabel(value) {
  if (!value) return '';
  const row = OUTREACH_OUTCOMES.find((o) => o.value === value);
  return row ? row.label : value;
}

export function outreachChannelLabel(value) {
  const row = OUTREACH_CHANNELS.find((c) => c.value === value);
  return row ? row.label : value || 'Other';
}

export async function fetchSponsorOutreachLog(sponsorRowId) {
  if (!sponsorRowId) return [];
  const q = query(
    collection(firestore, 'sponsors', sponsorRowId, OUTREACH_SUBCOLLECTION),
    orderBy('createdAt', 'desc'),
    limit(ENTRY_LIMIT)
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * @param {string} sponsorRowId
 * @param {{ body: string, channel: string, outcome?: string, nextFollowUpDate?: string | null }} fields nextFollowUpDate as yyyy-mm-dd
 * @param {import('firebase/auth').User | null} user
 */
export async function addSponsorOutreachLog(sponsorRowId, fields, user) {
  const trimmed = (fields.body || '').trim();
  if (!trimmed || !sponsorRowId) throw new Error('Outreach details and sponsor row are required.');
  const channel = CHANNEL_VALUES.has(fields.channel) ? fields.channel : 'other';
  const outcome =
    fields.outcome && ALLOWED_OUTCOMES.has(fields.outcome) ? fields.outcome : null;
  let nextFollowUpAt = null;
  const d = (fields.nextFollowUpDate || '').trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(d)) {
    nextFollowUpAt = Timestamp.fromDate(new Date(`${d}T12:00:00`));
  }
  const a = snapshotAuthor(user);
  const t = serverTimestamp();
  await addDoc(collection(firestore, 'sponsors', sponsorRowId, OUTREACH_SUBCOLLECTION), {
    body: trimmed,
    channel,
    outcome,
    nextFollowUpAt,
    createdAt: t,
    authorUid: a.authorUid,
    authorEmail: a.authorEmail,
    authorDisplayName: a.authorDisplayName,
  });
  const parentPayload = {
    lastOutreachAt: t,
    lastOutreachSummary: trimmed.slice(0, 200),
    lastOutreachChannel: channel,
    lastOutreachOutcome: outcome,
    lastTouchByDisplayName: a.authorDisplayName,
    lastTouchByUid: a.authorUid,
    lastTouchAt: t,
  };
  if (nextFollowUpAt) parentPayload.nextFollowUpAt = nextFollowUpAt;
  await updateDoc(doc(firestore, 'sponsors', sponsorRowId), parentPayload);
}

/**
 * Append a structured pipeline stage change entry to sponsorOutreachLog.
 * @param {string} sponsorRowId
 * @param {{ from: string, to: string, note?: string }} fields
 * @param {import('firebase/auth').User | null} user
 */
export async function addSponsorPipelineChangeLog(sponsorRowId, fields, user) {
  if (!sponsorRowId) throw new Error('Sponsor row is required.');
  const from = (fields?.from || '').trim();
  const to = (fields?.to || '').trim();
  const note = (fields?.note || '').trim();
  if (from === to && !note) return;
  const a = snapshotAuthor(user);
  const t = serverTimestamp();
  await addDoc(collection(firestore, 'sponsors', sponsorRowId, OUTREACH_SUBCOLLECTION), {
    type: 'pipeline_change',
    from,
    to,
    body: note || null,
    by: a.authorUid,
    at: t,
    createdAt: t,
    authorUid: a.authorUid,
    authorEmail: a.authorEmail,
    authorDisplayName: a.authorDisplayName,
  });
  await updateDoc(doc(firestore, 'sponsors', sponsorRowId), {
    lastTouchByDisplayName: a.authorDisplayName,
    lastTouchByUid: a.authorUid,
    lastTouchAt: t,
  });
}
